import axios, { AxiosError, AxiosResponse, CanceledError, InternalAxiosRequestConfig } from 'axios';
import { HTTP_STATUS } from '@/shared/constants/httpStatus';
import { isSessionExpired, markSessionExpired } from './sessionExpiry';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// タイムアウト定数（ミリ秒）- Nginxのproxy_read_timeoutと同期
const REQUEST_TIMEOUT_MS = 120000;

const LOGIN_PAGE_PATH = '/login';

// 2FAフロー中のエンドポイントは401でもリダイレクトしない
// - /api/auth/session: 認証チェック用なので正常な動作
// - /api/auth/verify-2fa: 2FAコード検証中（pendingToken期限切れの可能性）
// - /api/auth/resend-2fa: 2FAコード再送信中（pendingToken期限切れの可能性）
const REDIRECT_EXEMPT_ENDPOINTS: readonly string[] = [
  '/api/auth/session',
  '/api/auth/verify-2fa',
  '/api/auth/resend-2fa',
];

/**
 * 401 を受けたときにセッション切れとしてログイン画面へ移すかを判定する。
 * ログイン画面にいる場合（パスワード誤りの 401 など）と 2FA フロー中は移さない。
 * 既にセッション切れとして移動中の場合も、移動は1回だけにするため移さない。
 */
const shouldRedirectToLoginPage = ({ requestUrl }: { readonly requestUrl: string }): boolean => {
  const isRedirectExemptEndpoint = REDIRECT_EXEMPT_ENDPOINTS.some((endpoint) => requestUrl.includes(endpoint));
  const isOnLoginPage = window.location.pathname === LOGIN_PAGE_PATH;
  return !isRedirectExemptEndpoint && !isOnLoginPage && !isSessionExpired();
};

// Axiosインスタンスの作成
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: REQUEST_TIMEOUT_MS,
  withCredentials: true, // Cookieを送受信するために必須
});

// リクエストインターセプター：セッション切れの後は、ログイン画面へ移るまで送らない
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  if (isSessionExpired()) {
    throw new CanceledError('Session expired');
  }
  return config;
});

// レスポンスインターセプター：エラーハンドリング
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const isUnauthorized = error.response?.status === HTTP_STATUS.UNAUTHORIZED;
    if (isUnauthorized && shouldRedirectToLoginPage({ requestUrl: error.config?.url || '' })) {
      // 認証エラーの場合、ログイン画面へ
      // Cookieはサーバー側で削除される
      markSessionExpired();
      window.location.href = LOGIN_PAGE_PATH;
    }
    return Promise.reject(error);
  }
);

